/**
 * TurnActions - re-run, edit-and-run, refine, and the version toggle.
 *
 * THREE distinct actions, deliberately not collapsed:
 *   Re-run  a fresh attempt at the same question
 *   Edit    a different question - handed to the MAIN COMPOSER, since the
 *           composer is where questions are written; a second little textarea
 *           on the turn was a duplicate writing surface
 *   Refine  improve THIS answer, optionally saying how (stays inline: an
 *           instruction about an answer is not a question)
 *
 * All three belong to ONE assistant turn and act on it in place. A re-run is
 * not a new exchange: the transcript keeps its shape and the answer gains a
 * version, so the row sits under the answer it belongs to rather than
 * appearing as a fresh question further down.
 *
 * The toggle renders only when there is genuinely more than one answer —
 * every turn reports at least one (synthetic) version, so a bare "v1" chip on
 * every turn in the chat would be noise.
 *
 * FAILURE IS RENDERED HERE TOO, and that is the point: a first answer that
 * errored, a re-run the backend refused, and a version that would not load
 * are one situation to the reader - "this didn't work, let me try again" —
 * so they share one block and one Rerun button rather than three
 * presentations that happen to look similar. When `failure` is set the block
 * leads, with Rerun promoted from a quiet icon to the obvious next action.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  PiArrowClockwiseLight,
  PiPencilSimpleLight,
  PiSparkleLight,
  PiThumbsDownLight,
  PiThumbsUpLight,
  PiWarningCircleLight,
} from 'react-icons/pi';
import BrandLoader from '../../shared/brand/BrandLoader';
import { AssistantTurn } from '../turns';
import {
  VERSIONS_LABEL,
  defaultVersion,
  hasVersionHistory,
  versionFeedback,
  versionHint,
  versionLabel,
  versionQuery,
} from '../versions';

interface Props {
  turn: AssistantTurn;
  /** Re-run this answer; `query` replaces the question. */
  onRerun?: (messageUuid: string, query?: string) => void;
  /** Load this turn's question into the main composer for editing. */
  onEdit?: (messageUuid: string, query: string) => void;
  onSelectVersion?: (messageUuid: string, version: number) => void;
  /** Refine this answer; `instruction` is optional. */
  onRefine?: (messageUuid: string, instruction?: string) => void;
  /** Rate the answer. Clicking the active thumb clears it (`null`). */
  onRate?: (messageUuid: string, rating: 'up' | 'down' | null) => void;
  /** Hidden while the turn is producing an answer. */
  disabled?: boolean;
  /**
   * Why this turn has no usable answer.
   *
   * Set → the failure block renders. See `failureReason`, which is the single
   * decision point for what this says.
   */
  failure?: string;
}

const TurnActions: React.FC<Props> = ({
  turn, onRerun, onEdit, onSelectVersion, onRefine, onRate, disabled, failure,
}) => {
  const { t } = useTranslation();
  /** Only refine composes here now; editing happens in the main composer. */
  const [composing, setComposing] = useState<'refine' | null>(null);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const messageUuid = turn.messageUuid || '';
  const info = turn.versions;
  const active = turn.activeVersion ?? defaultVersion(info);

  useEffect(() => {
    if (composing) inputRef.current?.focus();
  }, [composing]);

  // Nothing to act on without a stored message: an optimistic turn has no
  // uuid yet, and re-running it would have nothing to address.
  if (!messageUuid || (!onRerun && !onSelectVersion && !onRate && !onRefine && !onEdit)) {
    return null;
  }

  /** Hand the question to the composer; the surface takes it from there. */
  const startEdit = () => onEdit?.(messageUuid, versionQuery(info, active));

  const startRefine = () => {
    // Deliberately blank: the instruction is OPTIONAL, and pre-filling it
    // would imply one is needed.
    setDraft('');
    setComposing('refine');
  };

  const submitCompose = () => {
    const text = draft.trim();
    setComposing(null);
    // Blank means "just improve it" - the action sends null.
    onRefine?.(messageUuid, text || undefined);
  };

  if (composing) {
    return (
      <div className="mt-2" data-testid="turn-refine">
        <p className="mb-1 text-[11px] text-fg-muted">
          {t('How should I improve this? (optional)')}
        </p>
        <textarea
          ref={inputRef}
          value={draft}
          rows={2}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitCompose(); }
            if (e.key === 'Escape') setComposing(null);
          }}
          data-testid="turn-refine-input"
          aria-label={t('How should I improve this? (optional)')}
          className="w-full px-2.5 py-2 rounded-lg text-sm bg-surface-sunken text-fg-body
                     border border-hairline-soft resize-none
                     focus:outline-none focus:border-champagne/50
                     transition-colors duration-200 ease-slide"
        />
        <div className="mt-1.5 flex items-center gap-2">
          <button
            type="button"
            onClick={submitCompose}
            data-testid="turn-refine-run"
            className="px-2.5 py-1 rounded-full text-xs font-medium
                       bg-champagne/15 border border-champagne/40 text-fg-strong
                       hover:border-champagne transition-colors duration-200 ease-slide"
          >
            {t('Refine')}
          </button>
          <button
            type="button"
            onClick={() => setComposing(null)}
            data-testid="turn-refine-cancel"
            className="px-2 py-1 rounded-full text-xs text-fg-muted hover:text-fg-strong
                       transition-colors duration-200 ease-slide"
          >
            {t('Cancel')}
          </button>
        </div>
      </div>
    );
  }

  /**
   * The version control - a glass segmented pill.
   *
   * The active segment is a single element that SLIDES between positions
   * rather than a class swapping on each button: one moving highlight reads
   * as one control with a state, where per-button backgrounds read as three
   * separate chips that happen to be adjacent. Segments are equal-width
   * (`flex-1` under a grid-like track) so the travel is uniform.
   */
  const versionCount = info?.versions.length || 0;
  const activeIndex = Math.max(0, info?.versions.findIndex((v) => v.number === active) ?? 0);

  const versionToggle = hasVersionHistory(info) && (
    <span className="inline-flex items-center gap-1.5" data-testid="version-control">
      {/* Named, because a row of bare numbers says nothing on its own. */}
      <span
        className="text-[10px] uppercase tracking-wide text-fg-muted"
        data-testid="version-control-label"
      >
        {t(VERSIONS_LABEL)}
      </span>
    <div
      className="relative inline-flex items-center glass-bar rounded-full
                 border border-hairline-soft p-0.5 overflow-hidden"
      role="group"
      aria-label={t(VERSIONS_LABEL)}
      data-testid="version-toggle"
    >
      {/* The sliding highlight. Inert to pointers so the segments stay
          clickable through it. */}
      <span
        aria-hidden
        data-testid="version-indicator"
        className="absolute top-0.5 bottom-0.5 start-0.5 rounded-full nav-pill nav-pill-active
                   pointer-events-none transition-transform duration-300 ease-slide"
        style={{
          width: `calc((100% - 0.25rem) / ${versionCount})`,
          transform: `translateX(${activeIndex * 100}%)`,
        }}
      />

      {info!.versions.map((version) => (
        <button
          key={version.uuid || version.number}
          type="button"
          onClick={() => onSelectVersion?.(messageUuid, version.number)}
          data-testid="version-option"
          data-version={version.number}
          data-active={version.number === active}
          aria-pressed={version.number === active}
          title={versionHint(version) || undefined}
          className={clsx(
            'relative z-10 flex-1 whitespace-nowrap px-2.5 py-0.5 rounded-full',
            'text-[11px] font-medium transition-colors duration-200 ease-slide',
            version.number === active
              ? 'text-fg-strong'
              : 'text-fg-muted hover:text-fg-strong'
          )}
        >
          {versionLabel(version)}
        </button>
      ))}

      {turn.versionLoading && (
        <span className="relative z-10 ps-1 pe-0.5"><BrandLoader size="xs" /></span>
      )}
    </div>
    </span>
  );

  /**
   * Thumbs. The ACTIVE thumb toggles off, so there is one control per
   * direction rather than a separate "clear" affordance.
   */
  // The rating of the version ON SCREEN, not of the message as a whole.
  const rating = versionFeedback(info, active, turn.feedback ?? null);
  const thumb = (
    direction: 'up' | 'down',
    Icon: typeof PiThumbsUpLight,
    label: string
  ) => (
    <button
      type="button"
      onClick={() => onRate?.(messageUuid, rating === direction ? null : direction)}
      data-testid={`turn-rate-${direction}`}
      data-active={rating === direction}
      aria-pressed={rating === direction}
      aria-label={label}
      title={label}
      className={clsx(
        'p-1 rounded-full transition-colors duration-200 ease-slide',
        rating === direction
          ? 'text-champagne bg-champagne/10'
          : 'text-fg-muted hover:text-fg-strong hover:bg-surface-sunken'
      )}
    >
      <Icon className="w-3.5 h-3.5" aria-hidden />
    </button>
  );

  const feedback = onRate && (
    <span className="inline-flex items-center gap-0.5" data-testid="turn-feedback">
      {thumb('up', PiThumbsUpLight, t('Good answer'))}
      {thumb('down', PiThumbsDownLight, t('Poor answer'))}
    </span>
  );

  if (failure) {
    return (
      <div
        className="mt-2 p-3 rounded-lg border border-danger-200 bg-danger-50/60
                   dark:border-danger-900/50 dark:bg-danger-900/15"
        data-testid="turn-failure"
      >
        <div className="flex items-start gap-2">
          <PiWarningCircleLight
            className="w-4 h-4 mt-0.5 text-danger-600 flex-shrink-0"
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-fg-strong">
              {t('This run did not complete.')}
            </p>
            <p className="mt-0.5 text-xs text-fg-body" data-testid="turn-failure-reason">
              {t(failure)}
            </p>

            {onRerun && (
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => onRerun(messageUuid)}
                  data-testid="turn-failure-rerun"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                             text-xs font-medium bg-champagne/20 border border-champagne/50
                             text-fg-strong hover:border-champagne
                             transition-colors duration-200 ease-slide"
                >
                  <PiArrowClockwiseLight className="w-3.5 h-3.5" aria-hidden />
                  {t('Re-run')}
                </button>
                {onEdit && <button
                  type="button"
                  onClick={startEdit}
                  data-testid="turn-failure-edit"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full
                             text-xs font-medium text-fg-muted hover:text-fg-strong
                             hover:bg-surface-sunken transition-colors duration-200 ease-slide"
                >
                  <PiPencilSimpleLight className="w-3.5 h-3.5" aria-hidden />
                  {t('Edit and run')}
                </button>}
                {/* The failed version stays selected; an earlier one that DID
                    work is one click away, never silently substituted. */}
                {versionToggle}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="turn-actions">
      {versionToggle}

      {onRerun && !disabled && (
        <button
          type="button"
          onClick={() => onRerun(messageUuid)}
          data-testid="turn-rerun"
          title={t('Run this question again')}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px]
                     text-fg-muted hover:text-fg-strong hover:bg-surface-sunken
                     transition-colors duration-200 ease-slide"
        >
          <PiArrowClockwiseLight className="w-3.5 h-3.5" aria-hidden />
          {t('Re-run')}
        </button>
      )}

      {onEdit && !disabled && (
        <button
          type="button"
          onClick={startEdit}
          data-testid="turn-edit-open"
          title={t('Edit the question in the composer')}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px]
                     text-fg-muted hover:text-fg-strong hover:bg-surface-sunken
                     transition-colors duration-200 ease-slide"
        >
          <PiPencilSimpleLight className="w-3.5 h-3.5" aria-hidden />
          {t('Edit')}
        </button>
      )}

      {onRefine && !disabled && (
        <button
          type="button"
          onClick={startRefine}
          data-testid="turn-refine-open"
          title={t('Improve this answer')}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px]
                     text-fg-muted hover:text-fg-strong hover:bg-surface-sunken
                     transition-colors duration-200 ease-slide"
        >
          <PiSparkleLight className="w-3.5 h-3.5" aria-hidden />
          {t('Refine')}
        </button>
      )}

      {/* Rating sits with the other per-answer actions. Offered whenever
          there is an answer to rate - including one the user has since
          re-run, since the rating is of THIS version. */}
      {!disabled && feedback}

    </div>
  );
};

export default TurnActions;
